import BrainSearchMessageSourceList from "@/brain-search/internal/BrainSearchMessageSourceList"
import BrainSearchRelatedQuestionList from "@/brain-search/internal/BrainSearchRelatedQuestionList"
import { BrainSearchAssistantMessageFragment$key } from "@/brain-search/internal/__generated__/BrainSearchAssistantMessageFragment.graphql"
import { GlobalID } from "@/relay/RelayTypes"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import DiscoEditor from "@components/editor/DiscoEditor"
import { DiscoDivider, DiscoIcon, DiscoText } from "@disco-ui"
import useFeatureFlags from "@utils/hook/useFeatureFlags"
import { TestIDProps } from "@utils/typeUtils"
import classNames from "classnames"
import { graphql, useFragment } from "react-relay"
import BrainSearchMessageFeedbackFooter from "./BrainSearchMessageFeedbackFooter"

interface Props extends TestIDProps {
  brainSearchMessageKey: BrainSearchAssistantMessageFragment$key
  generatingMessageId: GlobalID | null
  isLatestAssistantMessage?: boolean
  onCreateMessage: (prompt: string, sourceRelatedQuestionId?: GlobalID | null) => void
}

function BrainSearchAssistantMessage({
  brainSearchMessageKey,
  generatingMessageId,
  isLatestAssistantMessage = false,
  onCreateMessage,
}: Props) {
  const classes = useStyles()
  const { brainSearchFollowup } = useFeatureFlags()

  const message = useFragment<BrainSearchAssistantMessageFragment$key>(
    graphql`
      fragment BrainSearchAssistantMessageFragment on BrainSearchMessage {
        id
        messageText
        ...BrainSearchMessageFeedbackFooterFragment
        sources {
          totalCount
        }
        externalSources {
          totalCount
        }
      }
    `,
    brainSearchMessageKey
  )
  if (!message) return null

  const isGenerating = generatingMessageId === message.id
  const noInternalSources = message.sources?.totalCount === 0
  const hasExternalSources = message.externalSources?.totalCount !== 0

  return (
    <div key={message.id} className={classNames(classes.message, classes.fadeIn)}>
      {noInternalSources && hasExternalSources && (
        <>
          <div className={classes.webSearchInfoTag}>
            <DiscoIcon icon={"stars"} width={16} height={16} active />
            <DiscoText variant={"body-sm"} color={"text.secondary"}>
              {
                "No information was found in the community, the answer below is based on web search."
              }
            </DiscoText>
          </div>
          <DiscoDivider marginTop={0} marginBottom={0} />
        </>
      )}

      {/* Answer */}
      <DiscoEditor
        key={message.messageText}
        readOnly
        disableGutter
        markdown={message.messageText}
        fontSize={"16px"}
        disableScroll
        className={classes.editor}
      />
      {!isGenerating && (
        <BrainSearchMessageFeedbackFooter brainSearchMessageKey={message} />
      )}

      {/* Sources */}
      <BrainSearchMessageSourceList brainSearchMessageId={message.id} />

      {/* AI Generated Related Questions */}

      {!isGenerating &&
        isLatestAssistantMessage &&
        brainSearchFollowup &&
        brainSearchFollowup !== "Off" && (
          <BrainSearchRelatedQuestionList
            brainSearchMessageId={message.id}
            onCreateMessage={onCreateMessage}
          />
        )}
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  message: {
    width: "100%",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  editor: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.measure.borderRadius.big,
    background: "none",
  },
  fadeIn: {
    animation: "$fadeIn 0.5s",
  },
  // eslint-disable-next-line local-rules/disco-unused-classes
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
  webSearchInfoTag: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
  },
}))

export default BrainSearchAssistantMessage
